import { createContext, ReactNode, useContext, useEffect, useMemo, useRef } from "react";
import { FieldErrors, FieldValues, FormProvider, SubmitHandler, UseFormReturn } from "react-hook-form";
import { BlockerFunction } from "react-router-dom";
import styled from "styled-components";
import { ObjectSchema } from "yup";

import { ConfirmLeavePage } from "@/modules/irohana-components/commons/ConfirmLeavePage";
import { FCC } from "@/modules/irohana-components/types/common";
import { isEmpty } from "@/modules/irohana-components/utils/object";

type TFormProps<T extends FieldValues> = {
  methods: UseFormReturn<T>;
  onSubmit: SubmitHandler<T>;
  shouldBlock?: boolean | BlockerFunction;
  enableBlocker?: boolean;
  children: ReactNode;
  schema: ObjectSchema<object>;
  height?: string;
  customMessageLeave?: string;
  onBeforeSubmit?: () => void;
};

export const Form = <T extends FieldValues>({
  methods,
  onSubmit,
  shouldBlock = false,
  enableBlocker = true,
  children,
  schema,
  height,
  customMessageLeave,
  onBeforeSubmit,
}: TFormProps<T>) => {
  const ref = useRef<HTMLFormElement | null>(null);
  const {
    formState: { errors },
  } = methods;

  function getFirstErrorPath<T extends FieldValues>(errors: FieldErrors<T>, prefix = ""): string | null {
    for (const key in errors) {
      const value = errors[key];
      const path = prefix ? `${prefix}.${key}` : key;

      if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          const item = value[i];
          if (item && typeof item === "object" && Object.keys(item).length > 0) {
            const nested = getFirstErrorPath(item as FieldErrors<T>, `${path}.${i}`);
            if (nested) return nested;
          }
        }
      } else if (value && typeof value === "object") {
        if ("message" in value && "type" in value) {
          return path;
        } else {
          const nested = getFirstErrorPath(value as FieldErrors<T>, path);
          if (nested) return nested;
        }
      }
    }

    return null;
  }

  useEffect(() => {
    if (!ref.current || !errors) return;

    const firstErrorPath = getFirstErrorPath(errors);

    if (firstErrorPath) {
      const field = ref.current.querySelector(`[name="${firstErrorPath}"]`) as HTMLElement;
      field?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [errors, ref]);

  const isBlockedForm = useMemo(() => {
    if (methods.formState.isSubmitted && isEmpty(methods.formState.errors)) return false;
    if (isEmpty(methods.formState.dirtyFields)) return false;
    return true;
  }, [methods.formState]);

  return (
    <FormProvider {...methods}>
      <FormSchemaProvider schema={schema}>
        {enableBlocker && <ConfirmLeavePage shouldBlock={shouldBlock || isBlockedForm} customMessage={customMessageLeave} />}
        <StyledForm
          onSubmit={(e) => {
            e.stopPropagation();
            onBeforeSubmit?.();
            methods.handleSubmit(onSubmit)(e);
          }}
          ref={ref}
          height={height}
        >
          {children}
        </StyledForm>
      </FormSchemaProvider>
    </FormProvider>
  );
};

const FormSchemaContext = createContext<Record<string, TFieldMeta>>({});

type TFormSchemaProviderProps = {
  schema: ObjectSchema<object>;
};

export type TFieldMeta = {
  label: string;
  required: boolean;
  name: string;
  type: string;
  meta: {
    type?: string;
    isZipCode?: boolean;
    isFurigana?: boolean;
    autoTransformToHalfWidth?: boolean;
    autoTransformToFullWidth?: boolean;
    autoTransformToUpperCase?: boolean;
    removeSpaces?: boolean;
    autoTransformToHalfWidthForMatchLicenseNumber?: boolean;
    isPhone?: boolean;
    isEmptyToNull?: boolean;
    katakanaToHiragana?: boolean;
  };
};

const FormSchemaProvider: FCC<TFormSchemaProviderProps> = ({ children, schema }) => {
  const fieldsMeta = useMemo(() => {
    const { fields } = schema as ObjectSchema<Record<string, unknown>>;
    return Object.keys(fields).reduce(
      (cur, fieldName) => {
        const fieldMeta: TFieldMeta = { name: fieldName, required: false, label: "", type: "", meta: {} };
        try {
          if (fieldName in fields) {
            const data = fields[fieldName].describe();
            if ("label" in data && data.label) {
              fieldMeta.label = data.label;
            }
            if ("optional" in data && data.optional === false && "nullable" in data && data.nullable === false) {
              fieldMeta.required = true;
            }
            if ("type" in data && data.type) {
              fieldMeta.type = data.type;
            }
            if ("meta" in data && data.meta) {
              fieldMeta.meta = data.meta;
            }
          }
        } catch (error) {
          // do nothing
        }
        cur[fieldName] = fieldMeta;
        return cur;
      },
      {} as Record<string, TFieldMeta>,
    );
  }, [schema]);

  return <FormSchemaContext.Provider value={fieldsMeta}>{children}</FormSchemaContext.Provider>;
};

export const useFormSchema = () => {
  return useContext(FormSchemaContext);
};

const StyledForm = styled.form<{ height?: string }>`
  height: ${(props) => props.height};
`;
